import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { share, takeUntil } from 'rxjs';
import { ApiService } from 'src/app/services/api-services/api.service';
import { updateAuth } from 'src/app/store/auth/auth.actions';
import { selectAuth } from 'src/app/store/auth/auth.selector';
import { TokenAuthState } from 'src/app/store/auth/auth.state';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import { clearLocalStorage } from 'src/app/utility/local-storage';
import { environment } from 'src/environments/environment';
import {
  getFullName,
  getMyNameAcronym,
  getNameAcronym,
  getProfPic,
  isAppreciateEnabled,
  isNominationEnabled,
  showError,
} from '../../../utility/common-logic';
import { SecondaryOtpService } from 'src/app/services/secondary-otp.service';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
})
export class PanelComponent extends AutoUnsubscribeComponent implements OnInit {
  auth$: TokenAuthState;
  modules = environment.settings.modules;
  panel = environment.settings.panel;
  admin = environment.url.admin;
  isBSTLApp = environment.settings.app === 'bstl';
  myFeeds = environment.settings.myFeeds;
  freshchat = environment.settings.freshchat;
  websiteLink = environment.url.SSOUrl;
  goToSsoLinkOnLogout = environment.settings.goToSsoLinkOnLogout;
  goToAdminDashboard = environment.settings.goToAdminDashboard;
  public spinner: boolean = false;
  @Output() toggleHandler = new EventEmitter<boolean>();

  constructor(
    private store: Store,
    private router: Router,
    private api: ApiService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private secondaryOtpService: SecondaryOtpService,
    private shared: SharedService,
  ) {
    super();
  }

  async ngOnInit() {
    this.store
      .pipe(select(selectAuth), takeUntil(this.destroy$))
      .subscribe((auth) => {
        this.auth$ = auth;
      });
    if (this.modules.feeds) {
      await this.api
        .getUserFeed('approvals')
        .then((res) => {
          this.store.dispatch(
            updateAuth({
              payload: {
                approvalsCounter: res?.approvals_count,
                showApprovals: res.show_approvals,
              },
            }),
          );
        })
        .catch((err) => showError(err, this.toastr, this.translate));
    }
  }

  getAdminDeepLinkToken(event: Event) {
    if (!(this.auth$.user_details?.is_staff && this.goToAdminDashboard)) return;
    event.stopPropagation();

    this.spinner = true;

    this.api
      .getDeepLinkToken()
      .then((res) => {
        if (res?.deeplink_token) {
          const adminUrl = `${this.admin}/deep-link-login/${res.deeplink_token}`;
          window.open(adminUrl, '_blank', 'noopener,noreferrer');
        }
      })
      .catch((error) => showError(error, this.toastr, this.translate))
      .finally(() => {
        this.spinner = false;
        this.toggleHandler.emit(false);
      });
  }

  get profPic() {
    return getProfPic(this.auth$.user_details);
  }

  get acronym() {
    if (this.isBSTLApp) {
      return getNameAcronym(this.auth$.user_details?.department_name);
    } else {
      return getMyNameAcronym(this.auth$.user_details);
    }
  }

  get fullName() {
    return getFullName(this.auth$.user_details);
  }

  get isAppreciationOrNominationEnabled() {
    return (
      isAppreciateEnabled(this.auth$.organization_settings) ||
      isNominationEnabled(this.auth$.organization_settings)
    );
  }

  async signOut() {
    if (this.freshchat.isShow && !!(window as any).fcWidget) {
      (window as any).fcWidget.hide();
    }

    await this.api.logOut();

    this.secondaryOtpService.resetOtpVerification();
    clearLocalStorage();
    this.api.clearCacheStorage();

    if (this.goToSsoLinkOnLogout && this.websiteLink) {
      window.location.href = this.websiteLink;
    } else {
      this.router.navigate(['/login']);
    }
  }

  get actionClass() {
    let isShow = this.panel.link || this.auth$.user_details?.is_staff;
    isShow =
      isShow ||
      this.modules.feeds ||
      this.auth$.organization_settings?.enable_nominations === 1 ||
      this.modules.settings ||
      this.panel.support;
    return isShow ? 'show' : '';
  }

  goToUserFeeds() {
    if (this.router.url.includes('my-feed')) {
      window.location.reload();
    }
    this.router.navigate(['/my-feed']);
  }
}
